<script lang="ts">
  import { onDestroy, onMount } from "svelte";
  import {
    formDetailStore,
    formFieldsStore,
    patchForm,
    replaceFormFields,
  } from "@entities/form/store.svelte";
  import { sessionStore } from "@features/auth/session/model/store.svelte";
  import { navigate } from "@shared/router/navigation";
  import { canEditMasters } from "@shared/types/session";
  import type {
    Form,
    FormField,
    FormFieldInput,
    FormFieldType,
    FormStatus,
    JsonObject,
  } from "@shared/types/form";
  import { formatDateTime } from "@shared/util/format";

  interface Props {
    id: string;
  }

  type Tab = "settings" | "fields" | "preview" | "install";

  interface FieldDraft {
    clientId: string;
    id: string | null;
    field_type: FormFieldType;
    label: string;
    placeholder: string;
    is_required: boolean;
    optionsText: string;
    help_text: string;
    crm_target_key: string;
    originalCrmTargetKey: string | null;
  }

  interface OptionRow {
    label: string;
    value: string;
  }

  interface CrmTargetOption {
    category: string;
    label: string;
    key: string;
  }

  interface CrmTargetGroup {
    label: string;
    options: CrmTargetOption[];
  }

  const { id }: Props = $props();

  const fieldTypeOptions: { value: FormFieldType; label: string }[] = [
    { value: "text", label: "text" },
    { value: "email", label: "email" },
    { value: "tel", label: "tel" },
    { value: "textarea", label: "textarea" },
    { value: "select", label: "select" },
    { value: "radio", label: "radio" },
    { value: "checkbox", label: "checkbox" },
    { value: "consent", label: "consent" },
    { value: "hidden", label: "hidden" },
  ];

  const optionFieldTypes = new Set<FormFieldType>([
    "select",
    "radio",
    "checkbox",
  ]);
  const defaultOptionsText = '[\n  { "label": "選択肢", "value": "option" }\n]';
  const crmTargetGroups: CrmTargetGroup[] = [
    {
      label: "法律事務所",
      options: [
        {
          category: "基本情報",
          label: "正式名称",
          key: "firm.official_name",
        },
        {
          category: "基本情報",
          label: "通称名",
          key: "firm.common_name",
        },
        {
          category: "連絡先",
          label: "郵便番号",
          key: "firm.postal_code",
        },
        {
          category: "連絡先",
          label: "都道府県",
          key: "firm.prefecture",
        },
        {
          category: "連絡先",
          label: "市区町村",
          key: "firm.city",
        },
        {
          category: "連絡先",
          label: "番地・建物名",
          key: "firm.address_line",
        },
        {
          category: "連絡先",
          label: "電話番号",
          key: "firm.phone",
        },
        {
          category: "連絡先",
          label: "メールアドレス",
          key: "firm.email",
        },
        {
          category: "連絡先",
          label: "Webサイト",
          key: "firm.website_url",
        },
        {
          category: "所属",
          label: "弁護士会",
          key: "firm.bar_association",
        },
        {
          category: "基本情報",
          label: "取扱業務",
          key: "firm.practice_areas",
        },
      ],
    },
    {
      label: "弁護士",
      options: [
        {
          category: "基本情報",
          label: "氏名",
          key: "lawyer.full_name",
        },
        {
          category: "基本情報",
          label: "氏名（カナ）",
          key: "lawyer.full_name_kana",
        },
        {
          category: "所属",
          label: "修習地",
          key: "lawyer.training_location",
        },
        {
          category: "連絡先",
          label: "メールアドレス",
          key: "lawyer.email",
        },
        {
          category: "連絡先",
          label: "直通電話",
          key: "lawyer.direct_line",
        },
        {
          category: "連絡先",
          label: "個人サイト",
          key: "lawyer.personal_site_url",
        },
        {
          category: "基本情報",
          label: "出身大学",
          key: "lawyer.university",
        },
        {
          category: "基本情報",
          label: "ロースクール",
          key: "lawyer.law_school",
        },
        {
          category: "基本情報",
          label: "取扱業務",
          key: "lawyer.practice_areas",
        },
        {
          category: "基本情報",
          label: "主要SNS",
          key: "lawyer.major_sns",
        },
        {
          category: "基本情報",
          label: "コミュニティ種別",
          key: "lawyer.community_types",
        },
        {
          category: "基本情報",
          label: "副業意向",
          key: "lawyer.side_work_intention",
        },
        {
          category: "基本情報",
          label: "副業時間意向",
          key: "lawyer.side_work_time_intention",
        },
        {
          category: "基本情報",
          label: "副業報酬意向",
          key: "lawyer.side_work_compensation_intention",
        },
      ],
    },
    {
      label: "フォーム受信",
      options: [
        {
          category: "その他",
          label: "申込者種別",
          key: "applicant_type",
        },
      ],
    },
  ];
  const crmTargetKeys = new Set(
    crmTargetGroups.flatMap((group) => group.options.map((o) => o.key)),
  );

  let activeTab = $state<Tab>("settings");
  let settingsSaving = $state(false);
  let fieldsSaving = $state(false);
  let saveError = $state<string | null>(null);
  let saveMessage = $state<string | null>(null);
  let copyMessage = $state<string | null>(null);
  let syncedFormStamp = $state<string | null>(null);
  let syncedFieldsStamp = $state<string | null>(null);
  let nextClientId = 1;

  let formDraft = $state({
    slug: "",
    name: "",
    form_type: "",
    status: "draft" as FormStatus,
    settingsText: "{}",
  });

  let fieldDrafts = $state<FieldDraft[]>([]);

  let allowEdit = $derived(
    sessionStore.user ? canEditMasters(sessionStore.user.role) : false,
  );
  let busy = $derived(settingsSaving || fieldsSaving);

  $effect(() => {
    const form = formDetailStore.data;
    if (!form || form.id !== id) return;
    const stamp = `${form.id}:${form.updated_at}`;
    if (syncedFormStamp === stamp) return;
    formDraft = {
      slug: form.slug,
      name: form.name,
      form_type: form.form_type,
      status: form.status,
      settingsText: JSON.stringify(form.settings ?? {}, null, 2),
    };
    syncedFormStamp = stamp;
  });

  $effect(() => {
    const fields = formFieldsStore.data;
    if (fields === null) return;
    const stamp = fields
      .map((field) => `${field.id}:${field.sort_order}:${field.updated_at}`)
      .join("|");
    if (syncedFieldsStamp === stamp) return;
    fieldDrafts = fields.map(toFieldDraft);
    syncedFieldsStamp = stamp;
  });

  function parseJsonObject(raw: string, label: string): JsonObject {
    const text = raw.trim() || "{}";
    const value = JSON.parse(text) as unknown;
    if (value === null || Array.isArray(value) || typeof value !== "object") {
      throw new Error(`${label} は JSON object で入力してください`);
    }
    return value as JsonObject;
  }

  function parseOptions(raw: string): OptionRow[] {
    const value = JSON.parse(raw.trim()) as unknown;
    if (!Array.isArray(value) || value.length === 0) {
      throw new Error("options JSON は空でない配列にしてください");
    }
    return value.map((item) => {
      if (item === null || Array.isArray(item) || typeof item !== "object") {
        throw new Error("options JSON の各要素は object にしてください");
      }
      const rawLabel = (item as Record<string, unknown>).label;
      const rawValue = (item as Record<string, unknown>).value;
      const label = typeof rawLabel === "string" ? rawLabel.trim() : "";
      const valueText = typeof rawValue === "string" ? rawValue.trim() : "";
      if (!label || !valueText) {
        throw new Error("options JSON の label と value は必須です");
      }
      return { label, value: valueText };
    });
  }

  function emptyToNull(value: string): string | null {
    const trimmed = value.trim();
    return trimmed.length === 0 ? null : trimmed;
  }

  function statusLabel(status: FormStatus): string {
    if (status === "draft") return "下書き";
    if (status === "published") return "公開中";
    return "アーカイブ";
  }

  function statusTagClass(status: FormStatus): string {
    if (status === "published") return "tag tag-ok";
    if (status === "archived") return "tag tag-danger";
    return "tag tag-warn";
  }

  function isOptionField(fieldType: FormFieldType): boolean {
    return optionFieldTypes.has(fieldType);
  }

  function toFieldDraft(field: FormField): FieldDraft {
    return {
      clientId: field.id,
      id: field.id,
      field_type: field.field_type,
      label: field.label,
      placeholder: field.placeholder ?? "",
      is_required: field.is_required,
      optionsText:
        field.options === null || field.options === undefined
          ? ""
          : JSON.stringify(field.options, null, 2),
      help_text: field.help_text ?? "",
      crm_target_key: field.crm_target_key ?? "",
      originalCrmTargetKey: field.crm_target_key,
    };
  }

  function newFieldDraft(): FieldDraft {
    return {
      clientId: `new-${Date.now()}-${nextClientId++}`,
      id: null,
      field_type: "text",
      label: "新しい項目",
      placeholder: "",
      is_required: false,
      optionsText: "",
      help_text: "",
      crm_target_key: "",
      originalCrmTargetKey: null,
    };
  }

  function normalizeFieldDraft(draft: FieldDraft): FormFieldInput {
    const label = draft.label.trim();
    if (!label) throw new Error("フィールド名は必須です");
    const input: FormFieldInput = {
      id: draft.id ?? undefined,
      field_type: draft.field_type,
      label,
      placeholder: emptyToNull(draft.placeholder),
      is_required: draft.is_required,
      help_text: emptyToNull(draft.help_text),
    };
    if (isOptionField(draft.field_type)) {
      input.options = parseOptions(draft.optionsText);
    } else {
      input.options = null;
    }
    const crmTargetKey = emptyToNull(draft.crm_target_key);
    if (draft.id === null || crmTargetKey !== draft.originalCrmTargetKey) {
      input.crm_target_key = crmTargetKey;
    }
    return input;
  }

  function isKnownCrmTargetKey(key: string): boolean {
    return crmTargetKeys.has(key);
  }

  function crmTargetOptionLabel(option: CrmTargetOption): string {
    return `${option.category} / ${option.label} (${option.key})`;
  }

  function showUnsupportedCrmTarget(draft: FieldDraft): boolean {
    return (
      draft.crm_target_key.trim().length > 0 &&
      !isKnownCrmTargetKey(draft.crm_target_key)
    );
  }

  function missingCrmTargetWarning(draft: FieldDraft, form: Form): boolean {
    return (
      form.status === "published" &&
      draft.field_type !== "hidden" &&
      draft.crm_target_key.trim().length === 0
    );
  }

  function load(): void {
    saveError = null;
    saveMessage = null;
    copyMessage = null;
    formDetailStore.clear();
    formFieldsStore.clear();
    void formDetailStore.load(id);
    void formFieldsStore.load(id);
  }

  function backToList(ev: MouseEvent): void {
    if (ev.metaKey || ev.ctrlKey || ev.shiftKey || ev.altKey || ev.button !== 0)
      return;
    ev.preventDefault();
    navigate("/forms");
  }

  async function saveSettings(): Promise<void> {
    if (!allowEdit || settingsSaving) return;
    settingsSaving = true;
    saveError = null;
    saveMessage = null;
    try {
      await patchForm(id, {
        slug: formDraft.slug.trim(),
        name: formDraft.name.trim(),
        form_type: formDraft.form_type.trim(),
        status: formDraft.status,
        settings: parseJsonObject(formDraft.settingsText, "settings"),
      });
      saveMessage = "設定を保存しました。";
    } catch (err) {
      saveError = err instanceof Error ? err.message : String(err);
    } finally {
      settingsSaving = false;
    }
  }

  function addField(): void {
    fieldDrafts = [...fieldDrafts, newFieldDraft()];
  }

  function removeField(index: number): void {
    fieldDrafts = fieldDrafts.filter((_, i) => i !== index);
  }

  function moveField(index: number, direction: -1 | 1): void {
    const nextIndex = index + direction;
    if (nextIndex < 0 || nextIndex >= fieldDrafts.length) return;
    const next = [...fieldDrafts];
    const current = next[index]!;
    next[index] = next[nextIndex]!;
    next[nextIndex] = current;
    fieldDrafts = next;
  }

  function updateFieldType(draft: FieldDraft, ev: Event): void {
    const value = (ev.currentTarget as HTMLSelectElement)
      .value as FormFieldType;
    draft.field_type = value;
    if (isOptionField(value) && draft.optionsText.trim().length === 0) {
      draft.optionsText = defaultOptionsText;
    }
    if (!isOptionField(value)) {
      draft.optionsText = "";
    }
  }

  async function saveFields(): Promise<void> {
    if (!allowEdit || fieldsSaving) return;
    fieldsSaving = true;
    saveError = null;
    saveMessage = null;
    try {
      const inputs = fieldDrafts.map(normalizeFieldDraft);
      await replaceFormFields(id, inputs);
      saveMessage = "フィールドを保存しました。";
    } catch (err) {
      saveError = err instanceof Error ? err.message : String(err);
    } finally {
      fieldsSaving = false;
    }
  }

  function previewOptions(draft: FieldDraft): OptionRow[] {
    if (
      !isOptionField(draft.field_type) ||
      draft.optionsText.trim().length === 0
    )
      return [];
    try {
      return parseOptions(draft.optionsText);
    } catch {
      return [];
    }
  }

  function publicDefinitionUrl(form: Form): string {
    const path = `/public/forms/${form.id}`;
    if (typeof window === "undefined") return path;
    return `${window.location.origin}${path}`;
  }

  async function copyText(value: string): Promise<void> {
    copyMessage = null;
    try {
      await navigator.clipboard.writeText(value);
      copyMessage = "コピーしました。";
    } catch {
      copyMessage = "コピーできませんでした。";
    }
  }

  onMount(load);

  onDestroy(() => {
    formDetailStore.clear();
    formFieldsStore.clear();
  });
</script>

<div class="back-link">
  <a href="/forms" onclick={backToList}>フォーム一覧へ戻る</a>
</div>

{#if formDetailStore.loading && formDetailStore.data === null}
  <section class="panel"><p class="muted">読み込み中...</p></section>
{:else if formDetailStore.error}
  <section class="panel">
    <p class="error" role="alert">{formDetailStore.error}</p>
  </section>
{:else if formDetailStore.data}
  {@const form = formDetailStore.data}
  <header class="detail-head">
    <div>
      <div class="title-row">
        <h1>{form.name}</h1>
        <span class={statusTagClass(form.status)}
          >{statusLabel(form.status)}</span
        >
      </div>
      <p class="meta">
        <code>{form.slug}</code>
        <span>schema {form.schema_version}</span>
        <span>更新 {formatDateTime(form.updated_at)}</span>
      </p>
    </div>
  </header>

  {#if saveError}
    <p class="error" role="alert">{saveError}</p>
  {/if}
  {#if saveMessage}
    <p class="notice" role="status">{saveMessage}</p>
  {/if}

  <div class="tabs" role="tablist" aria-label="フォーム詳細">
    <button
      type="button"
      class:active={activeTab === "settings"}
      onclick={() => (activeTab = "settings")}
    >
      設定
    </button>
    <button
      type="button"
      class:active={activeTab === "fields"}
      onclick={() => (activeTab = "fields")}
    >
      フィールド
    </button>
    <button
      type="button"
      class:active={activeTab === "preview"}
      onclick={() => (activeTab = "preview")}
    >
      プレビュー
    </button>
    <button
      type="button"
      class:active={activeTab === "install"}
      onclick={() => (activeTab = "install")}
    >
      設置
    </button>
  </div>

  {#if activeTab === "settings"}
    <section class="panel">
      <form
        onsubmit={(ev) => {
          ev.preventDefault();
          void saveSettings();
        }}
      >
        <div class="panel-header">
          <h2>設定</h2>
          <button
            type="submit"
            class="btn btn-primary"
            disabled={!allowEdit || settingsSaving}
            title={allowEdit ? "" : "権限がありません"}
          >
            {settingsSaving ? "保存中..." : "保存"}
          </button>
        </div>
        <div class="form-grid">
          <label>
            <span>slug</span>
            <input
              bind:value={formDraft.slug}
              maxlength="120"
              required
              disabled={!allowEdit || busy}
              autocomplete="off"
            />
          </label>
          <label>
            <span>名称</span>
            <input
              bind:value={formDraft.name}
              maxlength="200"
              required
              disabled={!allowEdit || busy}
              autocomplete="off"
            />
          </label>
          <label>
            <span>種別</span>
            <input
              bind:value={formDraft.form_type}
              maxlength="80"
              required
              disabled={!allowEdit || busy}
              autocomplete="off"
            />
          </label>
          <label>
            <span>ステータス</span>
            <select bind:value={formDraft.status} disabled={!allowEdit || busy}>
              <option value="draft">下書き</option>
              <option value="published">公開中</option>
              <option value="archived">アーカイブ</option>
            </select>
          </label>
          <label class="wide">
            <span>settings JSON</span>
            <textarea
              bind:value={formDraft.settingsText}
              rows="10"
              disabled={!allowEdit || busy}
            ></textarea>
          </label>
        </div>
      </form>
    </section>
  {:else if activeTab === "fields"}
    <section class="panel">
      <div class="panel-header">
        <h2>フィールド</h2>
        <div class="header-actions">
          <button
            type="button"
            class="btn"
            disabled={!allowEdit || busy}
            title={allowEdit ? "" : "権限がありません"}
            onclick={addField}
          >
            + 追加
          </button>
          <button
            type="button"
            class="btn btn-primary"
            disabled={!allowEdit || fieldsSaving}
            title={allowEdit ? "" : "権限がありません"}
            onclick={() => void saveFields()}
          >
            {fieldsSaving ? "保存中..." : "保存"}
          </button>
        </div>
      </div>
      {#if formFieldsStore.loading && formFieldsStore.data === null}
        <p class="muted">読み込み中...</p>
      {:else if formFieldsStore.error}
        <p class="error" role="alert">{formFieldsStore.error}</p>
      {:else if fieldDrafts.length === 0}
        <p class="empty">フィールドはまだ登録されていません。</p>
      {:else}
        <div class="table-scroll">
          <table class="field-table">
            <thead>
              <tr>
                <th>順序</th>
                <th>type</th>
                <th>ラベル</th>
                <th>CRM連携先</th>
                <th>必須</th>
                <th>placeholder</th>
                <th>help</th>
                <th>options JSON</th>
                <th>操作</th>
              </tr>
            </thead>
            <tbody>
              {#each fieldDrafts as draft, index (draft.clientId)}
                <tr>
                  <td class="order-cell">
                    <span>{index + 1}</span>
                    <div class="order-actions">
                      <button
                        type="button"
                        class="btn tiny"
                        disabled={!allowEdit || busy || index === 0}
                        onclick={() => moveField(index, -1)}
                      >
                        上
                      </button>
                      <button
                        type="button"
                        class="btn tiny"
                        disabled={!allowEdit ||
                          busy ||
                          index === fieldDrafts.length - 1}
                        onclick={() => moveField(index, 1)}
                      >
                        下
                      </button>
                    </div>
                  </td>
                  <td>
                    <select
                      value={draft.field_type}
                      disabled={!allowEdit || busy}
                      onchange={(ev) => updateFieldType(draft, ev)}
                    >
                      {#each fieldTypeOptions as option (option.value)}
                        <option value={option.value}>{option.label}</option>
                      {/each}
                    </select>
                  </td>
                  <td>
                    <input
                      bind:value={draft.label}
                      maxlength="200"
                      required
                      disabled={!allowEdit || busy}
                    />
                  </td>
                  <td class="crm-target-cell">
                    <select
                      bind:value={draft.crm_target_key}
                      disabled={!allowEdit || busy}
                    >
                      <option value="">未設定</option>
                      {#if showUnsupportedCrmTarget(draft)}
                        <option value={draft.crm_target_key}>
                          未対応キー: {draft.crm_target_key}
                        </option>
                      {/if}
                      {#each crmTargetGroups as group (group.label)}
                        <optgroup label={group.label}>
                          {#each group.options as option (option.key)}
                            <option value={option.key}
                              >{crmTargetOptionLabel(option)}</option
                            >
                          {/each}
                        </optgroup>
                      {/each}
                    </select>
                    {#if showUnsupportedCrmTarget(draft)}
                      <small class="field-warning">未対応キー</small>
                    {:else if missingCrmTargetWarning(draft, form)}
                      <small class="field-warning">公開中: CRM連携先なし</small>
                    {/if}
                  </td>
                  <td class="check-cell">
                    <input
                      type="checkbox"
                      bind:checked={draft.is_required}
                      disabled={!allowEdit || busy}
                    />
                  </td>
                  <td>
                    <input
                      bind:value={draft.placeholder}
                      disabled={!allowEdit ||
                        busy ||
                        draft.field_type === "consent"}
                    />
                  </td>
                  <td>
                    <input
                      bind:value={draft.help_text}
                      disabled={!allowEdit || busy}
                    />
                  </td>
                  <td class="options-cell">
                    {#if isOptionField(draft.field_type)}
                      <textarea
                        bind:value={draft.optionsText}
                        rows="4"
                        disabled={!allowEdit || busy}
                      ></textarea>
                    {:else}
                      <span class="muted">-</span>
                    {/if}
                  </td>
                  <td>
                    <button
                      type="button"
                      class="btn btn-danger"
                      disabled={!allowEdit || busy}
                      onclick={() => removeField(index)}
                    >
                      削除
                    </button>
                  </td>
                </tr>
              {/each}
            </tbody>
          </table>
        </div>
      {/if}
    </section>
  {:else if activeTab === "preview"}
    <section class="panel">
      <div class="panel-header">
        <h2>プレビュー</h2>
      </div>
      {#if fieldDrafts.length === 0}
        <p class="empty">フィールドはまだ登録されていません。</p>
      {:else}
        <div class="preview-list">
          {#each fieldDrafts as draft (draft.clientId)}
            <label class="preview-field">
              <span>
                {draft.label}
                {#if draft.is_required}<b>必須</b>{/if}
              </span>
              {#if draft.field_type === "textarea"}
                <textarea rows="4" placeholder={draft.placeholder} disabled
                ></textarea>
              {:else if draft.field_type === "select"}
                <select disabled>
                  <option>{draft.placeholder || "選択してください"}</option>
                  {#each previewOptions(draft) as option (option.value)}
                    <option>{option.label}</option>
                  {/each}
                </select>
              {:else if draft.field_type === "radio"}
                <div class="choice-list">
                  {#each previewOptions(draft) as option (option.value)}
                    <label><input type="radio" disabled /> {option.label}</label
                    >
                  {/each}
                </div>
              {:else if draft.field_type === "checkbox"}
                <div class="choice-list">
                  {#each previewOptions(draft) as option (option.value)}
                    <label
                      ><input type="checkbox" disabled /> {option.label}</label
                    >
                  {/each}
                </div>
              {:else if draft.field_type === "consent"}
                <label class="inline-choice">
                  <input type="checkbox" disabled />
                  <span>{draft.help_text || draft.label}</span>
                </label>
              {:else if draft.field_type === "hidden"}
                <input type="text" value="hidden" disabled />
              {:else}
                <input
                  type={draft.field_type}
                  placeholder={draft.placeholder}
                  disabled
                />
              {/if}
              {#if draft.help_text && draft.field_type !== "consent"}
                <small>{draft.help_text}</small>
              {/if}
            </label>
          {/each}
        </div>
      {/if}
    </section>
  {:else}
    <section class="panel">
      <div class="panel-header">
        <h2>設置</h2>
      </div>
      <dl class="install-list">
        <div>
          <dt>フォームID</dt>
          <dd>
            <code>{form.id}</code>
            <button
              type="button"
              class="btn"
              onclick={() => void copyText(form.id)}
            >
              コピー
            </button>
          </dd>
        </div>
        <div>
          <dt>公開定義URL</dt>
          <dd>
            <code>{publicDefinitionUrl(form)}</code>
            <button
              type="button"
              class="btn"
              onclick={() => void copyText(publicDefinitionUrl(form))}
            >
              コピー
            </button>
          </dd>
        </div>
      </dl>
      {#if copyMessage}
        <p class="notice" role="status">{copyMessage}</p>
      {/if}
    </section>
  {/if}
{/if}

<style>
  .back-link {
    margin-bottom: 10px;
  }
  .back-link a {
    font-size: 13px;
  }
  .detail-head {
    display: flex;
    justify-content: space-between;
    gap: 16px;
    align-items: flex-start;
    margin-bottom: 12px;
  }
  .title-row {
    display: flex;
    align-items: center;
    gap: 10px;
    flex-wrap: wrap;
  }
  h1 {
    margin: 0;
    font-size: 1.35rem;
  }
  .meta {
    display: flex;
    gap: 10px;
    flex-wrap: wrap;
    margin: 4px 0 0;
    color: var(--muted);
    font-size: 12px;
  }
  .panel-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 12px;
  }
  .panel-header h2 {
    margin: 0;
    font-size: 1rem;
  }
  .header-actions {
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
  }
  .notice {
    color: #065f46;
    background: #ecfdf5;
    border: 1px solid #a7f3d0;
    border-radius: var(--radius);
    padding: 10px 12px;
    margin: 0 0 12px;
  }
  .form-grid {
    display: grid;
    grid-template-columns: repeat(4, minmax(160px, 1fr));
    gap: 12px;
  }
  .form-grid label,
  .preview-field {
    display: grid;
    gap: 4px;
  }
  .form-grid label span,
  .preview-field > span {
    color: var(--muted);
    font-size: 12px;
    font-weight: 700;
  }
  .form-grid .wide {
    grid-column: 1 / -1;
  }
  .form-grid input,
  .form-grid select,
  .form-grid textarea {
    width: 100%;
  }
  .field-table {
    min-width: 1360px;
  }
  .field-table input,
  .field-table select,
  .field-table textarea {
    width: 100%;
  }
  .field-table textarea {
    resize: vertical;
  }
  .field-table .check-cell input {
    width: auto;
  }
  .order-cell {
    width: 96px;
  }
  .order-actions {
    display: flex;
    gap: 4px;
    margin-top: 4px;
  }
  .tiny {
    padding: 3px 7px;
    font-size: 12px;
  }
  .options-cell {
    min-width: 260px;
  }
  .crm-target-cell {
    min-width: 300px;
  }
  .field-warning {
    display: block;
    margin-top: 4px;
    color: #92400e;
    font-size: 11px;
    font-weight: 700;
  }
  .preview-list {
    display: grid;
    gap: 14px;
    max-width: 720px;
  }
  .preview-field input,
  .preview-field select,
  .preview-field textarea {
    width: 100%;
  }
  .preview-field small {
    color: var(--muted);
  }
  .preview-field b {
    display: inline-block;
    margin-left: 6px;
    color: #991b1b;
    font-size: 11px;
  }
  .choice-list {
    display: grid;
    gap: 6px;
  }
  .choice-list label,
  .inline-choice {
    display: flex;
    align-items: center;
    gap: 6px;
    color: var(--text);
    font-size: 13px;
  }
  .choice-list input,
  .inline-choice input {
    width: auto;
  }
  .install-list {
    display: grid;
    gap: 12px;
    margin: 0;
  }
  .install-list div {
    display: grid;
    grid-template-columns: 150px minmax(0, 1fr);
    gap: 8px 12px;
    align-items: center;
  }
  .install-list dt {
    color: var(--muted);
    font-size: 12px;
    font-weight: 700;
  }
  .install-list dd {
    display: flex;
    min-width: 0;
    gap: 8px;
    align-items: center;
    margin: 0;
  }
  code {
    max-width: 100%;
    overflow-wrap: anywhere;
    font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
    font-size: 12px;
  }
  button:disabled,
  input:disabled,
  select:disabled,
  textarea:disabled {
    opacity: 0.65;
    cursor: not-allowed;
  }
  @media (max-width: 860px) {
    .form-grid {
      grid-template-columns: 1fr;
    }
    .install-list div {
      grid-template-columns: 1fr;
    }
    .install-list dd {
      align-items: flex-start;
      flex-direction: column;
    }
  }
</style>
